// The launch-day kiosk session: how the TV computer, which has no Google
// sign-in, holds its remote-view credential.
//
// Flow: an operator mints a one-time code on /launch ("Open in remote"),
// someone types it into the TV at /launch?code=XXXXXX, the app redeems it for a
// short-lived read-only token and keeps that token in sessionStorage. From then
// on the kiosk boots straight into the dashboard until the token expires.
// Storage is app-private (`recruiter_admin_app_*`, per CLAUDE.md) and session
// scoped: closing the TV browser drops the credential.

/** `?theme=dark|light` on /launch — a TYPABLE theme override for the TV, which
 *  renders without the sidebar and so has no theme toggle to click. Returns the
 *  requested theme, or null when the param is absent/garbage. The caller
 *  applies it through `applyTheme`, which persists to the same cookie/storage
 *  keys the pre-paint script reads, so it sticks across reloads. */
export function themeFromSearch(search: string): "dark" | "light" | null {
  const value = new URLSearchParams(search).get("theme");
  return value === "dark" || value === "light" ? value : null;
}

export interface LaunchSession {
  token: string;
  /** ISO expiry, from the redeem response. */
  expiresAt: string;
}

const STORAGE_KEY = "recruiter_admin_app_launch_token";

/** Group a code for display: "ABC123" → "ABC 123" (typed codes get re-normalized). */
export function formatLaunchCode(code: string): string {
  const clean = normalizeLaunchCode(code);
  return clean.length > 3 ? `${clean.slice(0, 3)} ${clean.slice(3)}` : clean;
}

/** What a human typed → the canonical code: uppercase, separators dropped.
 *  Mirrors the backend's `normalize_code` so both sides agree on tolerance. */
export function normalizeLaunchCode(raw: string): string {
  return raw
    .toUpperCase()
    .split("")
    .filter((ch) => /[A-Z0-9]/.test(ch))
    .join("");
}

/** How early we stop trusting a token, so we re-auth before, not after, the
 *  backend cuts us off. */
const EXPIRY_MARGIN_MS = 60 * 1000;

/** Longest delay we will arm a timer for. Well past the 12-hour token, and far
 *  under `setTimeout`'s 2^31-1 ms ceiling, which a garbage far-future expiry
 *  would overflow into firing IMMEDIATELY. */
const MAX_EXPIRY_DELAY_MS = 24 * 60 * 60 * 1000;

/** True while the session's expiry is still in the future (with a minute of
 *  slack so we re-auth before, not after, the backend cuts us off). */
export function isLaunchSessionLive(session: LaunchSession, now: Date = new Date()): boolean {
  const expires = Date.parse(session.expiresAt);
  if (Number.isNaN(expires)) return false;
  return expires - EXPIRY_MARGIN_MS > now.getTime();
}

/** Milliseconds until this session stops being live, for arming the retirement
 *  timer. The TV's token dies at a time we ALREADY KNOW (12 hours from redeem),
 *  so the kiosk retires it on that clock rather than by waiting for requests to
 *  start failing: an unattended screen otherwise refetches a dead credential
 *  every 15 seconds until somebody walks past it.
 *
 *  Never negative (an already-dead session retires on the next tick) and never
 *  beyond `MAX_EXPIRY_DELAY_MS`. */
export function launchSessionExpiryDelay(session: LaunchSession, now: Date = new Date()): number {
  const expires = Date.parse(session.expiresAt);
  if (Number.isNaN(expires)) return 0;
  const remaining = expires - EXPIRY_MARGIN_MS - now.getTime();
  return Math.min(Math.max(remaining, 0), MAX_EXPIRY_DELAY_MS);
}

export function readLaunchSession(storage: Storage = window.sessionStorage): LaunchSession | null {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LaunchSession>;
    if (typeof parsed.token !== "string" || typeof parsed.expiresAt !== "string") return null;
    const session: LaunchSession = { token: parsed.token, expiresAt: parsed.expiresAt };
    return isLaunchSessionLive(session) ? session : null;
  } catch {
    // Corrupt storage or a lockdown-mode browser: behave as signed out.
    return null;
  }
}

export function storeLaunchSession(
  session: LaunchSession,
  storage: Storage = window.sessionStorage,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage may be unavailable (private mode); the session then lives only
    // in memory for this page load, which still renders the dashboard.
  }
}

export function clearLaunchSession(storage: Storage = window.sessionStorage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
